import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { fmtMoney } from "../lib/format";
import DataTable from "../components/DataTable";
import EntityFormDialog from "../components/EntityFormDialog";

export default function Counterparties() {
  const { data: rows = [] } = useQuery({ queryKey: ["cp-exposure"], queryFn: () => api.get("/counterparties-exposure") });
  const [form, setForm] = useState(false);

  const cpFields = [
    { key: "name", label: "Counterparty Name", type: "text", required: true, colSpan: 2 },
    { key: "type", label: "Type", type: "select", options: ["Supplier", "Buyer", "Broker", "Bank", "Shipowner"], required: true },
    { key: "country", label: "Country", type: "text", required: true },
    { key: "rating", label: "Internal Rating", type: "select", options: ["AAA", "AA", "AA-", "A+", "A", "A-", "BBB", "BB"], default: "A" },
    { key: "credit_limit", label: "Credit Limit (USD)", type: "number", default: 10000000 },
    { key: "kyc_status", label: "KYC Status", type: "select", options: ["Verified", "Pending", "Expired"], default: "Verified" },
    { key: "group", label: "Group / Related Party", type: "text" },
  ];

  const cols = [
    { key: "name", label: "Counterparty", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "type", label: "Type" },
    { key: "rating", label: "Rating", render: (r) => <span className="font-mono text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">{r.rating}</span> },
    { key: "credit_limit", label: "Credit Limit", align: "right", render: (r) => fmtMoney(r.credit_limit) },
    { key: "total_exposure", label: "Exposure", align: "right", render: (r) => fmtMoney(r.total_exposure) },
    { key: "available", label: "Available", align: "right", render: (r) => <span className={r.available < 0 ? "text-rose-600" : "text-emerald-600"}>{fmtMoney(r.available)}</span> },
    { key: "utilisation_pct", label: "Utilisation", align: "right", render: (r) => (
      <div className="flex items-center gap-2 justify-end">
        <div className="w-24 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full ${r.breach ? "bg-rose-500" : r.utilisation_pct > 80 ? "bg-amber-500" : "bg-orange-500"}`} style={{ width: `${Math.min(r.utilisation_pct, 100)}%` }} />
        </div>
        <span className="font-mono text-xs w-12">{r.utilisation_pct}%</span>
        {r.breach && <AlertTriangle className="h-4 w-4 text-rose-600" />}
      </div>
    ) },
  ];

  return (
    <div data-testid="counterparties-page">
      <DataTable testid="counterparty-table" title="Counterparty Credit Exposure" columns={cols} rows={rows} csvName="counterparty_exposure"
        onAdd={() => setForm(true)} addLabel="New Counterparty" />
      <EntityFormDialog open={form} onClose={() => setForm(false)} title="New Counterparty"
        path="counterparties" fields={cpFields} testid="cp-form"
        transform={(p) => ({ ...p, related_party: !!p.group })} />
    </div>
  );
}
